import React, { useState, useEffect, useMemo } from "react";
import { Breadcrumb, Button, Card, Pagination, SectionHeader, Text } from "@zorni/components-lib";
import styles from "./Favorites.module.css";
import { SERVICE_TYPE } from "../../../../common/model/enum";
import { useFavoritePublications } from "./hooks/useFavoriteList";
import { useZorni } from "../../../hooks/useZorni";
import { Link, useNavigate } from "react-router-dom";
import { useToggleFavorite } from "./hooks/useToggleFavorite";
import { useRequireAuth } from "../../../hooks/useRequireAuth";
import { useTranslation } from "react-i18next";

const normalizeLocationKey = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const resolveLocations = (item) => {
  const rawLocations = Array.isArray(item?.locations)
    ? item.locations
    : Array.isArray(item?.cities)
    ? item.cities
    : Array.isArray(item?.tripProgramDetails)
    ? item.tripProgramDetails.map((step) => step?.city || step?.country)
    : [item?.city];

  const seen = new Set();

  return (rawLocations || []).filter((entry) => {
    const label = String(entry || "").trim();
    if (!label) return false;

    const key = normalizeLocationKey(label);
    if (!key || seen.has(key)) return false;

    seen.add(key);
    return true;
  });
};

const Favorites = () => {
  const { t } = useTranslation();
  const { userId } = useZorni();
  const navigate = useNavigate();
  const requireAuth = useRequireAuth();

  const [viewMode, setViewMode] = useState("liste");
  const [currentPage, setCurrentPage] = useState(0);
   const PAGE_SIZE = 10;

  // ⚡ Hook favoris : toggle + mutation
  const { toggleFavorite } = useToggleFavorite();

  // ⚡ Hook combiné API favoris + publications
  const { favorites, publications, totalPubPages, loading, error, refetch } =
    useFavoritePublications(userId, SERVICE_TYPE.TRIP, currentPage, PAGE_SIZE);

  // Liste en local pour update optimiste
  const [displayList, setDisplayList] = useState([]);

  useEffect(() => {
    if (!publications) return;

    // Empêche boucle infinie : ne set que si les données ont changé
    setDisplayList((prev) => {
      const prevString = JSON.stringify(prev);
      const newString = JSON.stringify(publications);

      if (prevString === newString) return prev; // rien ne change → pas de rerender
      return publications;
    });
  }, [publications]);

  const visibleFavoritesCount = displayList.length;
  const hasUnavailableFavorites = favorites.length > 0 && visibleFavoritesCount === 0;

  // 🔄 Changer vue liste/grille
  const toggleView = () => {
    setViewMode((prev) => (prev === "liste" ? "grille" : "liste"));
  };

  // ⭐ FAVORIS — Optimistic UI + backend toggle
  const handleUpdateFavorite = async (item) => {
    await requireAuth(async () => {
      // Update optimiste
      setDisplayList((prev) =>
        prev.map((p) =>
          p.id === item.id ? { ...p, isFavorite: !p.isFavorite } : p
        )
      );

      // Update backend
      await toggleFavorite(userId, item);
      await refetch();
    });
  };

  // 🧭 Navigation vers détail publication
  const handleClick = (item) => {
    navigate(`/trips/${item.id}/${item.tripDateId}`);
  };

  /* En-tête commun aux trois états, aligné sur « Mes voyages », le dossier de
     réservation et la page d'avis : fil d'Ariane puis SectionHeader. */
  const entete = (
    <>
      <Breadcrumb
        className={styles.breadcrumb}
        ariaLabel={t("trips.results.breadcrumb", "Fil d'Ariane")}
        linkComponent={Link}
        items={[
          { label: t("trips.results.home", "Accueil"), to: "/" },
          { label: t("favorites.title", "Mes favoris") },
        ]}
      />

      <SectionHeader
        size="page"
        className={styles.pageHeader}
        kicker={t("favorites.kicker", "Vos inspirations")}
        title={t("favorites.title", "Mes favoris")}
        subtitle={t(
          "favorites.subtitle",
          "Retrouvez les voyages que vous avez enregistrés pour les comparer, les suivre et y revenir à tout moment."
        )}
      />
    </>
  );

  // Loader / Erreur
  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.pageContainer}>
          {entete}

          <section className={styles.sectionShell}>
            <div className={styles.emptyState}>
              <Text as="p" size="md" weight="regular" color="muted">
                {t("favorites.loading", "Chargement…")}
              </Text>
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.pageContainer}>
          {entete}

          <section className={styles.sectionShell}>
            <div className={styles.emptyState}>
              <Text as="p" size="md" weight="regular" color="muted">
                {t("favorites.error", "Impossible de charger vos favoris.")}
              </Text>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageContainer}>
        {entete}

        <section className={styles.sectionShell}>
          <div className={styles.sectionHead}>
            <div>
              <Text
                as="h3"
                size="xxl"
                weight="semibold"
                color="default"
                className={styles.sectionTitle}
              >
                {t("favorites.sectionTitle", "Voyages favoris")}
              </Text>
              <Text
                as="p"
                size="sm"
                weight="regular"
                color="muted"
                className={styles.sectionCaption}
              >
                {t("favorites.shown", {
                  count: visibleFavoritesCount,
                  defaultValue: `${visibleFavoritesCount} voyage affiché`,
                })}
              </Text>
            </div>

            {displayList.length > 0 && (
              <div className={styles.viewToggle}>
                <Button
                  iconName={viewMode === "liste" ? "iconGrid" : "iconListe"}
                  iconPosition="left"
                  label={viewMode === "liste" ? t("favorites.grid", "Grille") : t("favorites.list", "Liste")}
                  size="small"
                  background="#ffffff"
                  textColor="#1cc3b2"
                  hoverColor="#e0f5f2"
                  onClick={toggleView}
                  variant="secondary"
                />
              </div>
            )}
          </div>

          {displayList.length === 0 ? (
            <div className={styles.emptyState}>
              <Text as="p" size="md" weight="regular" color="muted">
                {hasUnavailableFavorites
                  ? t("favorites.unavailable", "Vos favoris existent toujours, mais les voyages correspondants ne sont plus disponibles.")
                  : t("favorites.empty", "Vous n'avez encore rien ajouté à vos favoris.")}
              </Text>
            </div>
          ) : (
            <div
              className={
                viewMode === "grille" ? styles.cardGrid : styles.cardList
              }
            >
              {displayList.map((item) => (
                <div
                  key={item.id}
                  className={
                    viewMode === "grille" ? styles.gridCardWrap : styles.listCardWrap
                  }
                >
                  {(() => {
                    const locations = resolveLocations(item);

                    return (
                  <Card
                    item={{
                      ...item,
                      city: item?.city || locations.join(" · "),
                      locations,
                    }}
                    t={t}
                    type={viewMode}
                    showLocations
                    showActivities={false}
                    showOptions={false}
                    onClick={() => handleClick(item)}
                    onUpdateFavorite={() => handleUpdateFavorite(item)}
                  />
                    );
                  })()}
                </div>
              ))}
            </div>
          )}

          {totalPubPages > 1 && (
            <div className={styles.paginationWrap}>
              <Pagination
                totalPages={totalPubPages}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Favorites;
